/**
 * @file Settings-owned Recalls CSV import surface.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import {
    openCsvTextAttachmentPreview,
} from "../../shared/attachments/attachmentPreview.js";
import {
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

const MAX_IMPORT_CSV_BYTES = 1024 * 1024;
const MAX_IMPORT_ROWS = 1000;
const PRESERVE_LEGACY_VALUE = "__preserve_legacy__";
const ATTACHMENT_ACTION_BUTTON_CLASS =
    "standard-action-icon-btn hover:!text-[var(--dfc-brand-primary)] focus-visible:!text-[var(--dfc-brand-primary)]";

const COLUMN_ALIASES = Object.freeze({
    provider: ["provider", "providers"],
    recall_reason: ["recall_reason", "recall reason", "reason"],
    status: ["status", "recall status"],
});

const recallsImportState = {
    csv: "",
    error: "",
    fileName: "",
    fileHash: null,
    isBusy: false,
    providerMappings: {},
    reasonMappings: {},
    result: null,
    sourceLabel: "",
    sourceFileNamePreviouslyImported: false,
    statusMappings: {},
};

let importRecallsCsvOverride = null;
let importMappingMemoryOverride = null;
let csvPreviewObjectUrl = null;

/**
 * Overrides the import callable dependency for local isolated checks.
 * @param {Function|null} importFn Import function override.
 * @returns {void}
 */
export function setRecallsImportCallableForTesting(importFn) {
    importRecallsCsvOverride = typeof importFn === "function" ? importFn : null;
}

/**
 * Overrides the import mapping-memory callable for local isolated checks.
 * @param {Function|null} memoryFn Mapping-memory function override.
 * @returns {void}
 */
export function setRecallsImportMappingMemoryCallableForTesting(memoryFn) {
    importMappingMemoryOverride =
        typeof memoryFn === "function" ? memoryFn : null;
}

async function runImportRecallsCsv(payload) {
    if (importRecallsCsvOverride) {
        return importRecallsCsvOverride(payload);
    }
    const module = await import("../recalls/recallsApi.js");
    return module.importRecallsCsv(payload);
}

async function runGetRecallImportMappingMemory(payload) {
    if (importMappingMemoryOverride) {
        return importMappingMemoryOverride(payload);
    }
    const module = await import("../recalls/recallsApi.js");
    return module.getRecallImportMappingMemory(payload);
}

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function revokeCsvPreviewObjectUrl() {
    if (csvPreviewObjectUrl) {
        URL.revokeObjectURL(csvPreviewObjectUrl);
    }
    csvPreviewObjectUrl = null;
}

function getCsvPreviewObjectUrl() {
    if (!recallsImportState.csv) {return null;}
    if (!csvPreviewObjectUrl) {
        const blob = new Blob(
            [recallsImportState.csv],
            { type: "text/csv;charset=utf-8" },
        );
        csvPreviewObjectUrl = URL.createObjectURL(blob);
    }
    return csvPreviewObjectUrl;
}

function openCsvPreview() {
    const csvUrl = getCsvPreviewObjectUrl();
    if (!csvUrl) {return;}
    const title = recallsImportState.fileName || "Recalls import CSV";
    openCsvTextAttachmentPreview({
        title,
        csvText: recallsImportState.csv,
        downloadUrl: csvUrl,
        openUrl: csvUrl,
    });
}

function normalizeHeader(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/^\uFEFF/, "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
}

function parseCsvRows(csv) {
    const rows = [];
    let row = [];
    let cell = "";
    let inQuotes = false;

    for (let index = 0; index < csv.length; index++) {
        const char = csv[index];
        const nextChar = csv[index + 1];

        if (inQuotes) {
            if (char === "\"" && nextChar === "\"") {
                cell += "\"";
                index++;
            } else if (char === "\"") {
                inQuotes = false;
            } else {
                cell += char;
            }
            continue;
        }

        if (char === "\"") {
            inQuotes = true;
        } else if (char === ",") {
            row.push(cell);
            cell = "";
        } else if (char === "\n") {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = "";
        } else if (char !== "\r") {
            cell += char;
        }
    }

    if (inQuotes) {
        throw new Error("CSV contains an unclosed quote.");
    }
    row.push(cell);
    rows.push(row);
    return rows.filter((csvRow) =>
        csvRow.some((value) => String(value || "").trim()));
}

function parseCsv(csv) {
    const rows = parseCsvRows(csv);
    if (rows.length === 0) {
        return { headers: [], records: [] };
    }
    const headers = rows[0].map((header) => String(header || "").trim());
    const records = rows.slice(1).map((values, index) => {
        const cells = {};
        headers.forEach((header, headerIndex) => {
            cells[header] = values[headerIndex] || "";
        });
        return { rowNumber: index + 2, cells };
    });
    return { headers, records };
}

function getDuplicateNormalizedHeaders(headers = []) {
    const counts = new Map();
    headers.forEach((header) => {
        const normalized = normalizeHeader(header);
        counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });
    return Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([header]) => header || "blank header");
}

function findHeader(headers, aliases) {
    const normalizedToHeader = new Map(
        headers.map((header) => [normalizeHeader(header), header]),
    );
    return aliases
        .map((alias) => normalizedToHeader.get(normalizeHeader(alias)))
        .find(Boolean) || null;
}

function getUniqueColumnValues(parsed, field) {
    const header = findHeader(parsed.headers, COLUMN_ALIASES[field]);
    if (!header) {return [];}
    const values = new Map();
    parsed.records.forEach((record) => {
        const value = String(record.cells[header] || "").trim();
        if (value && !values.has(value)) {
            values.set(value, value);
        }
    });
    return Array.from(values.values()).sort((a, b) => a.localeCompare(b));
}

function getImportPreview() {
    const csv = recallsImportState.csv || "";
    if (!csv.trim()) {
        return {
            byteLength: 0,
            error: "",
            headers: [],
            providerValues: [],
            reasonValues: [],
            records: [],
            statusValues: [],
        };
    }

    const byteLength = new Blob([csv]).size;
    try {
        const parsed = parseCsv(csv);
        return {
            ...parsed,
            byteLength,
            duplicateHeaders: getDuplicateNormalizedHeaders(parsed.headers),
            error: "",
            providerValues: getUniqueColumnValues(parsed, "provider"),
            reasonValues: getUniqueColumnValues(parsed, "recall_reason"),
            statusValues: getUniqueColumnValues(parsed, "status"),
        };
    } catch (error) {
        return {
            byteLength,
            duplicateHeaders: [],
            error: error.message || "CSV could not be parsed.",
            headers: [],
            providerValues: [],
            reasonValues: [],
            records: [],
            statusValues: [],
        };
    }
}

function getRecallStatusOptions() {
    return (Array.isArray(tempConfigData.recallStatus)
        ? tempConfigData.recallStatus
        : [])
        .filter((status) => status?.id)
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map((status) => ({
            value: String(status.id),
            label: status.name || status.id,
        }));
}

function getDefaultStatusMapping(rawValue) {
    const stored = recallsImportState.statusMappings[rawValue];
    if (stored) {return stored;}
    const normalized = normalizeHeader(rawValue);
    const matched = getRecallStatusOptions().find((status) =>
        normalizeHeader(status.value) === normalized ||
        normalizeHeader(status.label) === normalized);
    return matched?.value || "";
}

function getRecallReasonOptions() {
    const reasons = Array.isArray(tempConfigData.recalls?.recallReasons)
        ? tempConfigData.recalls.recallReasons
        : [];
    return reasons
        .filter((reason) => reason?.id && reason?.isActive !== false)
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map((reason) => ({
            value: String(reason.id),
            label: reason.name || reason.label || reason.id,
        }));
}

function getDefaultReasonMapping(rawValue) {
    const stored = recallsImportState.reasonMappings[rawValue];
    if (Array.isArray(stored) || stored) {return stored;}
    const normalized = normalizeHeader(rawValue);
    const matched = getRecallReasonOptions().find((reason) =>
        normalizeHeader(reason.value) === normalized ||
        normalizeHeader(reason.label) === normalized);
    return matched ? [matched.value] : [];
}

function getProviderOptions() {
    return (appData.users || [])
        .filter((user) => user?.id && user.isAppProvider === true)
        .map((user) => ({
            value: user.id,
            label: user.name || user.displayName || user.email || user.id,
            isActive: user.isActive !== false,
        }))
        .sort((a, b) => {
            const activeDiff = (a.isActive === false ? 1 : 0) -
                (b.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            return a.label.localeCompare(b.label);
        });
}

function getDefaultProviderMapping(rawValue) {
    const stored = recallsImportState.providerMappings[rawValue];
    if (stored) {return stored;}
    return "";
}

function isStatusMappingValid(value) {
    return getRecallStatusOptions().some((status) => status.value === value);
}

function getValidReasonMapping(value) {
    const values = Array.isArray(value) ? value : [value];
    const validReasonIds = new Set(
        getRecallReasonOptions().map((reason) => reason.value),
    );
    return values.filter((entry) => validReasonIds.has(entry));
}

function isProviderMappingValid(value) {
    if (value === PRESERVE_LEGACY_VALUE) {return true;}
    return getProviderOptions().some((provider) => provider.value === value);
}

function applyMappingMemory(memory = {}) {
    Object.entries(memory.statusMappings || {}).forEach(([rawValue, value]) => {
        if (!recallsImportState.statusMappings[rawValue] &&
            isStatusMappingValid(value)) {
            recallsImportState.statusMappings[rawValue] = value;
        }
    });

    Object.entries(memory.reasonMappings || {}).forEach(([rawValue, value]) => {
        if (recallsImportState.reasonMappings[rawValue]) {return;}
        const validReasons = getValidReasonMapping(value);
        if (validReasons.length > 0) {
            recallsImportState.reasonMappings[rawValue] = validReasons;
        }
    });

    Object.entries(memory.providerMappings || {}).forEach(([rawValue, value]) => {
        if (!recallsImportState.providerMappings[rawValue] &&
            isProviderMappingValid(value)) {
            recallsImportState.providerMappings[rawValue] = value;
        }
    });
    recallsImportState.sourceFileNamePreviouslyImported =
        memory.sourceFileNamePreviouslyImported === true;
}

async function applySavedMappingMemory(preview) {
    if (!preview || preview.error) {return;}
    const memory = await runGetRecallImportMappingMemory({
        providerValues: preview.providerValues || [],
        reasonValues: preview.reasonValues || [],
        sourceFileName: recallsImportState.fileName || null,
        statusValues: preview.statusValues || [],
    });
    applyMappingMemory(memory);
}

function renderSourceControls(preview) {
    const overLimit = preview.byteLength > MAX_IMPORT_CSV_BYTES ||
        preview.records.length > MAX_IMPORT_ROWS;
    const fileName = recallsImportState.fileName || "No file selected";
    return `
        <div class="grid gap-4 xl:grid-cols-[minmax(0,20rem)_minmax(30rem,1fr)]">
            <div class="space-y-1">
                <label class="block text-sm font-semibold text-gray-700" for="recalls-import-source-label">
                    Source label
                </label>
                <input
                    id="recalls-import-source-label"
                    type="text"
                    placeholder="Add a metadata label for this import"
                    value="${attrValue(recallsImportState.sourceLabel)}"
                    class="${NATIVE_INPUT_CLASSES} !h-[30px] !min-h-[30px] !py-0"
                    data-recalls-import-source-label
                >
            </div>
            <div class="min-w-0 space-y-1">
                <label class="block text-sm font-semibold text-gray-700">
                    File data
                </label>
                <div class="flex h-[30px] min-h-[30px] min-w-0 items-center gap-x-6 overflow-hidden rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                    <span><span class="font-semibold text-gray-900">Rows:</span> ${preview.records.length}</span>
                    <span><span class="font-semibold text-gray-900">Columns:</span> ${preview.headers.length}</span>
                    <span><span class="font-semibold text-gray-900">Bytes:</span> ${preview.byteLength}</span>
                    <span class="min-w-0 truncate" title="${attrValue(fileName)}">
                        <span class="font-semibold text-gray-900">File name:</span> ${escapeHTML(fileName)}
                    </span>
                </div>
                ${overLimit ? `
                    <div class="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                        CSV exceeds the MVP import limit.
                    </div>
                ` : ""}
            </div>
        </div>
    `;
}

function renderStatusMappings(statusValues) {
    return renderMappingTable({
        emptyMessage: "No status values found in the current CSV.",
        sourceHeading: "CSV status",
        targetHeading: "Recalls status",
        markerAttribute: "data-recalls-import-status-map",
        values: statusValues,
    });
}

function renderReasonMappings(reasonValues) {
    return renderMappingTable({
        emptyMessage: "No recall reason values found in the current CSV.",
        sourceHeading: "CSV recall reason",
        targetHeading: "Recalls reason",
        markerAttribute: "data-recalls-import-reason-map",
        values: reasonValues,
    });
}

function renderProviderMappings(providerValues) {
    return renderMappingTable({
        emptyMessage: "No provider values found in the current CSV.",
        sourceHeading: "CSV provider",
        targetHeading: "Import as",
        markerAttribute: "data-recalls-import-provider-map",
        values: providerValues,
    });
}

function renderMappingTable(config) {
    const {
        emptyMessage,
        markerAttribute,
        sourceHeading,
        targetHeading,
        values,
    } = config;
    if (values.length === 0) {
        return `
            <p class="settings-helper-text">${escapeHTML(emptyMessage)}</p>
        `;
    }
    return `
        <div class="overflow-x-auto rounded-md border border-gray-100">
            <table class="min-w-[30rem] table-fixed text-left text-sm w-full">
                <thead class="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                        <th class="w-[44%] px-3 py-2">${escapeHTML(sourceHeading)}</th>
                        <th class="w-[56%] px-3 py-2">${escapeHTML(targetHeading)}</th>
                    </tr>
                </thead>
                <tbody>
                    ${values.map((rawValue) => `
            <tr class="border-t border-gray-100">
                <td class="min-w-0 px-3 py-2 font-medium text-gray-800">
                    <span class="block truncate" title="${attrValue(rawValue)}">${escapeHTML(rawValue)}</span>
                </td>
                <td class="px-3 py-2">
                    <div class="min-w-0" ${markerAttribute}="${attrValue(rawValue)}"></div>
                </td>
            </tr>
        `).join("")}
                </tbody>
            </table>
        </div>
    `;
}

function updateMappingRowState(root, isMapped) {
    root.closest("tr")?.classList.toggle("bg-emerald-50", Boolean(isMapped));
}

function renderImportResult() {
    if (recallsImportState.error) {
        return `
            <div class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                ${escapeHTML(recallsImportState.error)}
            </div>
        `;
    }
    const result = recallsImportState.result;
    if (!result) {return "";}
    const importedCount = result.importedRowCount || 0;
    const unresolvedCount = result.unresolvedRowCount || 0;
    const acceptedWarningCount = result.warningAcceptedRowCount || 0;
    const blockingCount = result.blockingErrorRowCount || 0;
    const acceptReadyCount = Math.max(0, unresolvedCount - blockingCount);
    return `
        <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
            <div class="font-semibold text-gray-900">Import complete</div>
            <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div class="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                    <div class="text-xs font-medium uppercase text-emerald-700">Imported</div>
                    <div class="mt-1 text-lg font-semibold">${importedCount}</div>
                </div>
                <div class="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                    <div class="text-xs font-medium uppercase text-emerald-700">Warnings accepted</div>
                    <div class="mt-1 text-lg font-semibold">${acceptedWarningCount}</div>
                </div>
                <div class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                    <div class="text-xs font-medium uppercase text-amber-700">Can be accepted</div>
                    <div class="mt-1 text-lg font-semibold">${acceptReadyCount}</div>
                </div>
                <div class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-900">
                    <div class="text-xs font-medium uppercase text-red-700">Blocked</div>
                    <div class="mt-1 text-lg font-semibold">${blockingCount}</div>
                </div>
            </div>
            ${result.errorCsv ? `
                <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100"
                    data-recalls-import-download-errors
                >
                    <span class="material-symbols-outlined text-[18px]">download</span>
                    <span>Download unresolved CSV</span>
                </button>
            ` : ""}
        </div>
    `;
}

function validateImportPreview(preview) {
    if (!recallsImportState.csv.trim()) {
        return "Upload a CSV file before importing.";
    }
    if (preview.error) {return preview.error;}
    if (preview.duplicateHeaders?.length > 0) {
        return `CSV headers must be unique. Duplicate headers: ${preview.duplicateHeaders.join(", ")}.`;
    }
    if (preview.records.length === 0) {
        return "CSV must include at least one data row.";
    }
    if (preview.byteLength > MAX_IMPORT_CSV_BYTES) {
        return "CSV is too large for the MVP importer.";
    }
    if (preview.records.length > MAX_IMPORT_ROWS) {
        return `CSV supports ${MAX_IMPORT_ROWS} rows or fewer.`;
    }
    return "";
}

/**
 * Renders the admin-only Recalls CSV import surface.
 * @returns {string} Import settings HTML.
 */
export function renderRecallsImportSettings() {
    const preview = getImportPreview();
    const validationMessage = validateImportPreview(preview);
    const hasCsv = Boolean(recallsImportState.csv.trim());
    const disabled = Boolean(
        !hasCsv ||
        validationMessage ||
        recallsImportState.isBusy,
    );
    return `
        <div class="space-y-5" data-recalls-import-settings>
            <div class="flex items-center justify-between gap-3 border-b pb-2">
                <h4 class="text-lg font-semibold">CSV Import</h4>
                <div class="flex items-center gap-2">
                    ${hasCsv ? `
                        <button
                            type="button"
                            class="${ATTACHMENT_ACTION_BUTTON_CLASS}"
                            title="Open CSV preview"
                            aria-label="Open CSV preview"
                            data-recalls-import-open-preview
                        >
                            <span class="material-symbols-outlined text-[21px] leading-none">open_in_full</span>
                        </button>
                    ` : `
                        <button
                            type="button"
                            class="${ATTACHMENT_ACTION_BUTTON_CLASS}"
                            title="Upload CSV"
                            aria-label="Upload CSV"
                            data-recalls-import-upload-trigger
                        >
                            <span class="material-symbols-outlined text-[21px] leading-none">upload</span>
                        </button>
                    `}
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        class="sr-only"
                        data-recalls-import-file
                    >
                </div>
            </div>
            ${renderSourceControls(preview)}
            ${hasCsv ? `
                <div class="grid items-start gap-5 xl:grid-cols-2 2xl:grid-cols-3">
                <div>
                    <h5 class="mb-3 text-sm font-semibold text-gray-900">Status mapping</h5>
                    ${renderStatusMappings(preview.statusValues)}
                </div>
                <div>
                    <h5 class="mb-3 text-sm font-semibold text-gray-900">Recall reason mapping</h5>
                    ${renderReasonMappings(preview.reasonValues)}
                </div>
                <div>
                    <h5 class="mb-3 text-sm font-semibold text-gray-900">Provider mapping</h5>
                    ${renderProviderMappings(preview.providerValues)}
                </div>
                </div>
            ` : ""}
            ${validationMessage && hasCsv ? `
                <div class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    ${escapeHTML(validationMessage)}
                </div>
            ` : ""}
            ${renderImportResult()}
            <div class="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md bg-[var(--dfc-brand-primary)] px-3 py-2 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-recalls-import-run
                    ${disabled ? "disabled" : ""}
                >
                    <span class="material-symbols-outlined text-[18px]">upload_file</span>
                    <span>${recallsImportState.isBusy ? "Importing..." : "Import CSV"}</span>
                </button>
                <button
                    type="button"
                    class="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                    data-recalls-import-clear
                >Clear</button>
            </div>
        </div>
    `;
}

async function sha256Text(text) {
    if (!window.crypto?.subtle || !text) {return null;}
    const encoded = new TextEncoder().encode(text);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", encoded);
    return Array.from(new Uint8Array(hashBuffer))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

function downloadCsv(csv, fileName) {
    const blob = new Blob([csv || ""], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "Recalls Import Unresolved.csv";
    link.className = "hidden";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatDownloadTimestamp(date = new Date()) {
    const parts = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ];
    const timeParts = [
        String(date.getHours()).padStart(2, "0"),
        String(date.getMinutes()).padStart(2, "0"),
        String(date.getSeconds()).padStart(2, "0"),
    ];
    return `${parts.join("-")} ${timeParts.join("-")}`;
}

function appendTimestampToFileName(fileName) {
    const name = fileName || "Recalls Import Unresolved.csv";
    const timestamp = formatDownloadTimestamp();
    const extensionIndex = name.toLowerCase().lastIndexOf(".csv");
    if (extensionIndex === -1) {
        return `${name} ${timestamp}.csv`;
    }
    return `${name.slice(0, extensionIndex)} ${timestamp}${name.slice(extensionIndex)}`;
}

function buildImportPayload() {
    const statusMappings = {};
    Object.entries(recallsImportState.statusMappings).forEach(([raw, value]) => {
        if (value) {
            statusMappings[raw] = value;
        }
    });
    const providerMappings = {};
    Object.entries(recallsImportState.providerMappings).forEach(([raw, value]) => {
        if (value) {
            providerMappings[raw] = value;
        }
    });
    const reasonMappings = {};
    Object.entries(recallsImportState.reasonMappings).forEach(([raw, value]) => {
        const values = Array.isArray(value) ? value.filter(Boolean) : [];
        if (values.length > 0) {
            reasonMappings[raw] = values;
        }
    });
    return {
        csv: recallsImportState.csv,
        providerMappings,
        reasonMappings,
        sourceFileHash: recallsImportState.fileHash,
        sourceFileName: recallsImportState.fileName || null,
        sourceLabel: recallsImportState.sourceLabel ||
            recallsImportState.fileName ||
            null,
        statusMappings,
    };
}

function formatImportError(error) {
    const errors = Array.isArray(error?.details?.errors)
        ? error.details.errors
        : [];
    if (errors.length === 0) {
        return error?.message || "Recalls CSV import failed.";
    }
    const messages = errors
        .map((entry) => {
            if (typeof entry === "string") {return entry;}
            return entry?.message || entry?.field || "";
        })
        .filter(Boolean);
    if (messages.length === 0) {
        return error?.message || "Recalls CSV import failed.";
    }
    return `${error?.message || "Recalls CSV import failed."} ${messages.join(" ")}`;
}

function confirmRepeatedFileNameIfNeeded() {
    if (!recallsImportState.sourceFileNamePreviouslyImported) {
        return Promise.resolve(true);
    }
    const fileName = recallsImportState.fileName || "this file";
    return new Promise((resolve) => {
        openConfirmationModal(
            "File Already Imported",
            `A file named <strong>${escapeHTML(fileName)}</strong> has already been imported. You can still continue if this is intentional.`,
            () => {
                resolve(true);
            },
            false,
            "Import anyway",
            () => {
                resolve(false);
            },
        );
    });
}

/**
 * Binds Recalls import controls after the Settings tab renders.
 * @param {HTMLElement} container Settings tab container.
 * @param {object} options Bind options.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {void}
 */
export function bindRecallsImportSettings(container, options = {}) {
    const { rerenderCurrentTabContent = () => {} } = options;
    const importRoot = container.querySelector("[data-recalls-import-settings]");

    container.querySelector("[data-recalls-import-upload-trigger]")
        ?.addEventListener("click", () => {
            container.querySelector("[data-recalls-import-file]")?.click();
        });

    container.querySelector("[data-recalls-import-open-preview]")
        ?.addEventListener("click", openCsvPreview);

    container.querySelector("[data-recalls-import-file]")
        ?.addEventListener("change", async (event) => {
            const file = event.target.files?.[0];
            if (!file) {return;}
            const text = await file.text();
            revokeCsvPreviewObjectUrl();
            recallsImportState.csv = text;
            recallsImportState.fileName = file.name || "";
            recallsImportState.sourceLabel = "";
            recallsImportState.fileHash = await sha256Text(text);
            recallsImportState.error = "";
            recallsImportState.result = null;
            recallsImportState.sourceFileNamePreviouslyImported = false;
            try {
                await applySavedMappingMemory(getImportPreview());
            } catch (error) {
                console.error("Failed to load Recalls import mapping memory:", error);
            }
            rerenderCurrentTabContent();
        });

    container.querySelector("[data-recalls-import-source-label]")
        ?.addEventListener("input", (event) => {
            recallsImportState.sourceLabel = event.target.value || "";
        });

    const statusOptions = getRecallStatusOptions();
    container.querySelectorAll("[data-recalls-import-status-map]")
        .forEach((root) => {
            const rawValue = root.dataset.recallsImportStatusMap;
            createSelectControl({
                root,
                accessibleName: `Map status ${rawValue}`,
                value: getDefaultStatusMapping(rawValue),
                options: statusOptions,
                clearable: true,
                searchable: true,
                portalMenu: true,
                menuBoundary: importRoot,
                placeholder: "Select status mapping",
                emptyMessage: "No Recalls statuses configured.",
                onChange: (value) => {
                    recallsImportState.statusMappings[rawValue] = value || "";
                    updateMappingRowState(root, Boolean(value));
                },
            });
            updateMappingRowState(
                root,
                Boolean(getDefaultStatusMapping(rawValue)),
            );
        });

    const reasonOptions = getRecallReasonOptions();
    container.querySelectorAll("[data-recalls-import-reason-map]")
        .forEach((root) => {
            const rawValue = root.dataset.recallsImportReasonMap;
            createSelectControl({
                root,
                accessibleName: `Map recall reason ${rawValue}`,
                value: getDefaultReasonMapping(rawValue),
                options: reasonOptions,
                multiple: true,
                checkboxMenu: true,
                clearable: true,
                searchable: true,
                portalMenu: true,
                menuBoundary: importRoot,
                placeholder: "Select recall reason mapping",
                emptySelectionLabel: "Select recall reason mapping",
                selectedCountLabel: "Selected reasons",
                emptyMessage: "No Recalls reasons configured.",
                onChange: (value) => {
                    recallsImportState.reasonMappings[rawValue] =
                        Array.isArray(value) ? value : [];
                    updateMappingRowState(root, Array.isArray(value) &&
                        value.length > 0);
                },
            });
            updateMappingRowState(
                root,
                getDefaultReasonMapping(rawValue).length > 0,
            );
        });

    const providerOptions = [
        { value: PRESERVE_LEGACY_VALUE, label: "Preserve as legacy provider" },
        ...getProviderOptions().map((provider) => ({
            value: provider.value,
            label: `${provider.label}${provider.isActive ? "" : " (inactive)"}`,
        })),
    ];
    container.querySelectorAll("[data-recalls-import-provider-map]")
        .forEach((root) => {
            const rawValue = root.dataset.recallsImportProviderMap;
            createSelectControl({
                root,
                accessibleName: `Map provider ${rawValue}`,
                value: getDefaultProviderMapping(rawValue),
                options: providerOptions,
                clearable: true,
                searchable: true,
                portalMenu: true,
                menuBoundary: importRoot,
                placeholder: "Select provider mapping",
                emptyMessage: "No app providers configured.",
                onChange: (value) => {
                    recallsImportState.providerMappings[rawValue] =
                        value || "";
                    updateMappingRowState(root, Boolean(value));
                },
            });
            updateMappingRowState(
                root,
                Boolean(getDefaultProviderMapping(rawValue)),
            );
        });
}

/**
 * Handles Recalls import button actions.
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler options.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {Promise<boolean>} True when handled.
 */
export async function handleRecallsImportSettingsButtonClick(
    target,
    options = {},
) {
    const { rerenderCurrentTabContent = () => {} } = options;

    if (target.closest("[data-recalls-import-clear]")) {
        revokeCsvPreviewObjectUrl();
        Object.assign(recallsImportState, {
            csv: "",
            error: "",
            fileName: "",
            fileHash: null,
            isBusy: false,
            providerMappings: {},
            reasonMappings: {},
            result: null,
            sourceLabel: "",
            sourceFileNamePreviouslyImported: false,
            statusMappings: {},
        });
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest("[data-recalls-import-download-errors]")) {
        downloadCsv(
            recallsImportState.result?.errorCsv,
            appendTimestampToFileName("Recalls Import Unresolved.csv"),
        );
        return true;
    }

    if (target.closest("[data-recalls-import-run]")) {
        const preview = getImportPreview();
        const validationMessage = validateImportPreview(preview);
        if (validationMessage) {
            recallsImportState.error = validationMessage;
            rerenderCurrentTabContent();
            return true;
        }

        const shouldContinue = await confirmRepeatedFileNameIfNeeded();
        if (!shouldContinue) {
            return true;
        }

        recallsImportState.isBusy = true;
        recallsImportState.error = "";
        recallsImportState.result = null;
        rerenderCurrentTabContent();

        try {
            recallsImportState.result = await runImportRecallsCsv(
                buildImportPayload(),
            );
        } catch (error) {
            recallsImportState.error = formatImportError(error);
        } finally {
            recallsImportState.isBusy = false;
            rerenderCurrentTabContent();
        }
        return true;
    }

    return false;
}
